// frontend/src/components/briefing/briefing-cards.ts
//
// The pure half of the Command Briefing's screen one (plan 02 section 6): what
// each card's footer shows, its accessible name, which cards changed since the
// last poll, where "Approve it" goes, which server answer a chip or typed
// question reads, the poll delay, and the bell's links and times. The server
// writes every sentence; nothing here composes copy beyond joining it.

import type {
  BriefingCardModel,
  ServerAnswer,
  ServerBriefing,
} from './briefing-model'
import { WORKING_CARD_LINE } from './briefing-copy'

export const DEFAULT_POLL_MS = 10_000
export const APPROVALS_HREF = '/procurement/approvals'

// ---- cards -------------------------------------------------------------------

export type CardFooter =
  | { variant: 'needs_you'; pill: string; label: string }
  | { variant: 'working'; pill: string; label: string }
  | { variant: 'completed'; pill: string; label: '' }

/** The footer of a card (plan 11 A.1.11 B14): the button only. A filled button
 * when the card needs a decision, an outline "See progress" while a coworker
 * works, or the green "Completed" pill. `label` is the status word, kept for
 * the accessible name; the card no longer prints it. */
export function cardFooter(card: BriefingCardModel): CardFooter {
  if (card.status === 'completed') return { variant: 'completed', pill: card.pill || 'Completed', label: '' }
  if (card.status === 'working') return { variant: 'working', pill: card.pill || 'See progress', label: card.status_label }
  return { variant: 'needs_you', pill: card.pill, label: card.status_label || 'Needs your decision' }
}

export type CardAskLine =
  | { kind: 'needs_you'; text: string }
  | { kind: 'working'; text: string }

/** The line above the button (plan 11 A.1.6): "Your decision: ..." beside the
 * blue "!" on a card that needs a decision, the fixed "Working. Nothing is
 * needed from you yet." while a coworker works, nothing once completed (or on
 * an older server that sends no `ask`). */
export function cardAskLine(card: BriefingCardModel): CardAskLine | null {
  if (card.status === 'working') return { kind: 'working', text: WORKING_CARD_LINE }
  if (card.status === 'needs_you' && card.ask) return { kind: 'needs_you', text: card.ask }
  return null
}

/** "FACILITIES MANAGEMENT · STAGE 1 OF 6, DISCOVER. Your facilities management
 * contracts are up for renewal. Your decision: confirm ..." */
export function cardAriaLabel(card: BriefingCardModel): string {
  const ask = cardAskLine(card)
  const tail = ask ? ask.text.replace(/\.$/, '') : card.status_label || card.pill
  return `${card.kicker}. ${card.title}. ${tail}.`
}

/** One key per card state: a new key on a poll is an arrival (the 5 s glow). */
export function cardKeys(cards: readonly BriefingCardModel[] | null | undefined): string[] {
  return (cards ?? []).map((card) => `${card.id}:${card.state}`)
}

/** True when any card's state differs between two polls (the bell re-reads). */
export function cardsChanged(
  previous: readonly BriefingCardModel[] | null | undefined,
  next: readonly BriefingCardModel[] | null | undefined
): boolean {
  if (!previous) return false
  return cardKeys(previous).join('|') !== cardKeys(next).join('|')
}

/** Where "Approve it" opens: the first card that needs the person, else My Approvals. */
export function firstWaitingHref(cards: readonly BriefingCardModel[] | null | undefined): string {
  return (cards ?? []).find((card) => card.status === 'needs_you')?.href ?? APPROVALS_HREF
}

/** The coworkers sentence under the cards, or null when the server has none
 * (nothing loaded). The page prints it as is (plan 11 A.1.8). */
export function coworkersRowLine(data: { line?: string | null } | null | undefined): string | null {
  const line = (data?.line ?? '').trim()
  return line ? line : null
}

/** The face status badge (the face pile is no longer drawn; kept for the bell). */
export function faceBadge(status: 'working' | 'waiting' | 'done'): 'working' | 'needs_you' | null {
  if (status === 'working') return 'working'
  if (status === 'waiting') return 'needs_you'
  return null
}

// ---- answers -----------------------------------------------------------------

export type ServerIntent = 'needs' | 'pipeline' | 'today'

/** The server's answer for a chip, or null when the block has none (an older API). */
export function serverAnswer(server: ServerBriefing | null | undefined, intent: ServerIntent): ServerAnswer | null {
  const answer = server?.answers?.[intent]
  return answer && typeof answer.text === 'string' ? answer : null
}

/** The next poll: the server's pace (3 s while a card works), else 10 s. */
export function nextPollDelay(server: ServerBriefing | null | undefined): number {
  const ms = server?.poll_ms
  return typeof ms === 'number' && Number.isFinite(ms) && ms >= 1000 ? ms : DEFAULT_POLL_MS
}

/** True when the server sent the six-stage screen one (cards or Megan's line). */
export function hasScreenOne(server: ServerBriefing | null | undefined): boolean {
  return Array.isArray(server?.cards) || Boolean(server?.megan)
}

// ---- the bell ----------------------------------------------------------------

export interface BellEntry {
  type: string
  link_type?: string | null
  link_id?: string | null
  link_href?: string | null
  created_at?: string | null
}

/** Where a bell entry opens: its own link wins; PR and PO entries keep their routes. */
export function bellHref(entry: BellEntry, basePath = ''): string | null {
  if (entry.link_href) {
    return entry.link_href.startsWith('/') ? `${basePath}${entry.link_href}` : entry.link_href
  }
  if (!entry.link_type || !entry.link_id) return null
  if (entry.link_type === 'pr') return `${basePath}/procurement/requests/${entry.link_id}`
  if (entry.link_type === 'po') return `${basePath}/procurement/orders/${entry.link_id}`
  return null
}

/** The database stores naive UTC; a time without a zone is read as UTC. */
export function parseServerTime(value: string | null | undefined): Date | null {
  if (!value) return null
  const zoned = /[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`
  const ms = Date.parse(zoned)
  return Number.isNaN(ms) ? null : new Date(ms)
}

const LONDON = 'Europe/London'

function londonParts(date: Date): { day: string; time: string; label: string } {
  const day = date.toLocaleDateString('en-GB', { timeZone: LONDON, year: 'numeric', month: '2-digit', day: '2-digit' })
  const time = date.toLocaleTimeString('en-GB', { timeZone: LONDON, hour: '2-digit', minute: '2-digit', hour12: false })
  const weekday = date.toLocaleDateString('en-GB', { timeZone: LONDON, weekday: 'short' })
  const dayNum = date.toLocaleDateString('en-GB', { timeZone: LONDON, day: 'numeric' })
  const month = date.toLocaleDateString('en-GB', { timeZone: LONDON, month: 'short' })
  return { day, time, label: `${weekday} ${dayNum} ${month}` }
}

/** "11:12" on the demo day, "Fri 9 Oct" (or a planned "Tue 17 Nov") otherwise.
 * Never "3h ago": fast-forward stamps are in the future of the demo clock. */
export function bellTime(createdAt: string | null | undefined, now: Date): string {
  const at = parseServerTime(createdAt)
  if (!at) return ''
  const a = londonParts(at)
  const n = londonParts(now)
  return a.day === n.day ? a.time : a.label
}

export type BellIconKind = 'needs_you' | 'done' | 'info' | 'error'

/** The solid status icon of a bell entry (no amber: a warning reads as info). */
export function bellIcon(type: string): BellIconKind {
  if (type === 'needs_you') return 'needs_you'
  if (type === 'completed' || type === 'success') return 'done'
  if (type === 'error') return 'error'
  return 'info'
}

// ---- the ask box's routing (plan 02 section 6.7) -------------------------------

export type AskPlan =
  /** Render the server's answer; `navigateTo` opens after the delay. */
  | { kind: 'server'; intent: ServerIntent; answer: ServerAnswer; navigateTo: string | null; delayMs: number }
  /** Megan's approve line, then the first waiting card. Nothing is recorded. */
  | { kind: 'approve'; href: string; delayMs: number }
  /** Ask the rehearsed-answer endpoint (no model anywhere). */
  | { kind: 'rehearsed'; question: string; supply: boolean }
  /** Plain navigation ("open the desk"). */
  | { kind: 'navigate'; href: string }
  /** The older client-side answer (a server without screen one). */
  | { kind: 'legacy' }

export interface AskIntentLike {
  kind: string
  question?: string
  ref?: string
}

/**
 * What the ask box does with a question, in order: approve never decides and
 * opens the first waiting card; the three chips read the server's answers;
 * navigation intents navigate; anything else, supply questions included, goes
 * to the rehearsed endpoint first.
 */
export function askPlan(
  intent: AskIntentLike,
  server: ServerBriefing | null | undefined,
  delays: { pipeline: number; approve: number } = { pipeline: 1500, approve: 900 }
): AskPlan {
  const six = hasScreenOne(server)
  switch (intent.kind) {
    case 'approve':
      return six ? { kind: 'approve', href: firstWaitingHref(server?.cards), delayMs: delays.approve } : { kind: 'legacy' }
    case 'needs':
    case 'today':
    case 'pipeline': {
      const answer = serverAnswer(server, intent.kind)
      if (!answer) return intent.kind === 'pipeline' ? { kind: 'navigate', href: '/tender' } : { kind: 'legacy' }
      const navigateTo = intent.kind === 'pipeline' ? answer.navigate_to ?? '/tender' : null
      return { kind: 'server', intent: intent.kind, answer, navigateTo, delayMs: navigateTo ? delays.pipeline : 0 }
    }
    case 'desk':
      return { kind: 'navigate', href: '/supply-chain' }
    case 'orders':
      return { kind: 'navigate', href: '/supply-chain/orders' }
    case 'open':
      return { kind: 'navigate', href: `/tender/${encodeURIComponent(intent.ref ?? '')}` }
    case 'supply-question':
      return { kind: 'rehearsed', question: intent.question ?? '', supply: true }
    default:
      return { kind: 'rehearsed', question: intent.question ?? '', supply: false }
  }
}

/** The pill under a rehearsed answer that opens a page, by answer. */
export const OPEN_LABEL_BY_ANSWER: Record<string, string> = {
  'pulse-oximeters': 'Open on the Supply Chain Desk',
  'leo-soft-fm': "Open Leo's contract action",
  'eastgate-deliveries': 'Open the Eastgate trial',
  'fm-regime': 'Open the FM record',
}
